import { Helmet } from 'react-helmet-async';

const Books = () => {
  const books = [
    {
      id: 1,
      title: "Chronicles of the Shadow Realm",
      description: "In a world where shadows hold power and ancient magic resurfaces, a young apprentice discovers she possesses abilities that could either save her kingdom or plunge it into eternal darkness.",
      genre: "Epic Fantasy",
      status: "Ongoing",
      platforms: [
        { name: "Royal Road", icon: "fas fa-crown", url: "https://www.royalroad.com" },
        { name: "Wattpad", icon: "fas fa-book", url: "https://www.wattpad.com" },
        { name: "Amazon Kindle", icon: "fab fa-amazon", url: "https://www.amazon.com" }
      ]
    },
    {
      id: 2,
      title: "Starlight Legacy",
      description: "A space opera adventure following the crew of the starship 'Odyssey' as they uncover a conspiracy that threatens the entire galactic federation and discover a legacy that spans millennia.",
      genre: "Science Fiction",
      status: "Completed",
      platforms: [
        { name: "Rakuten Kobo", icon: "fas fa-tablet-alt", url: "https://www.kobo.com" },
        { name: "Royal Road", icon: "fas fa-crown", url: "https://www.royalroad.com" },
        { name: "Wattpad", icon: "fas fa-book", url: "https://www.wattpad.com" },
        { name: "Amazon Kindle", icon: "fab fa-amazon", url: "https://www.amazon.com" }
      ]
    },
    {
      id: 3,
      title: "The Last Alchemist",
      description: "In a steampunk-inspired world, a disgraced alchemist must rediscover lost arts to prevent a catastrophic energy crisis while evading powerful enemies who want her knowledge for themselves.",
      genre: "Steampunk Fantasy",
      status: "Ongoing",
      platforms: [
        { name: "Royal Road", icon: "fas fa-crown", url: "https://www.royalroad.com" },
        { name: "Wattpad", icon: "fas fa-book", url: "https://www.wattpad.com" },
        { name: "Rakuten Kobo", icon: "fas fa-tablet-alt", url: "https://www.kobo.com" }
      ]
    }
  ];

  const bookSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Books by Bungen Lee",
    "description": "Collection of fantasy and science fiction books by author Bungen Lee",
    "itemListElement": books.map((book, index) => ({
      "@type": "Book",
      "position": index + 1,
      "name": book.title,
      "description": book.description,
      "genre": book.genre,
      "author": {
        "@type": "Person",
        "name": "Bungen Lee"
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Books by Bungen Lee - Available on Royal Road, Wattpad, Kobo & More</title>
        <meta 
          name="description" 
          content="Explore all books by Bungen Lee. Available on Royal Road, Wattpad, Rakuten Kobo, Amazon Kindle, and other platforms. Fantasy and science fiction adventures await." 
        />
        <link rel="canonical" href="https://yourusername.github.io/bungen-lee-website/#/books" />
        <meta property="og:title" content="Books by Bungen Lee - Available on Multiple Platforms" />
        <meta property="og:description" content="Discover fantasy and science fiction books by Bungen Lee on Royal Road, Wattpad, Rakuten Kobo, and more." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://yourusername.github.io/bungen-lee-website/#/books" />
        <script type="application/ld+json">
          {JSON.stringify(bookSchema)}
        </script>
      </Helmet>

      <section className="books-hero">
        <div className="container">
          <div className="section-header fade-in-up">
            <h1>My Books</h1>
            <p>Discover my collection of fantasy and science fiction stories available across multiple platforms</p>
          </div>
        </div>
      </section>

      <section className="books-list">
        <div className="container">
          <div className="grid grid-2">
            {books.map(book => (
              <div key={book.id} className="book-card card fade-in-up">
                <div className="book-cover">
                  <i className="fas fa-scroll"></i>
                </div>
                
                <div className="book-info">
                  <div className="book-meta">
                    <span className="book-genre">{book.genre}</span>
                    <span className={`book-status ${book.status.toLowerCase()}`}>
                      {book.status}
                    </span>
                  </div>
                  
                  <h3>{book.title}</h3>
                  <p>{book.description}</p>
                  
                  <div className="book-links">
                    {book.platforms.map((platform, index) => (
                      <a
                        key={index}
                        href={platform.url}
                        className="platform-link"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className={platform.icon}></i>
                        {platform.name}
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="platforms-info">
        <div className="container">
          <div className="section-header">
            <h2>Available Platforms</h2>
            <p>Read wherever you prefer</p>
          </div>
          
          <div className="grid grid-3">
            <div className="platform-card card">
              <div className="platform-icon">
                <i className="fas fa-crown"></i>
              </div>
              <h4>Royal Road</h4>
              <p>Read ongoing serials and interact with the community</p>
            </div>
            
            <div className="platform-card card">
              <div className="platform-icon">
                <i className="fas fa-book"></i>
              </div>
              <h4>Wattpad</h4>
              <p>Mobile-friendly reading with social features</p>
            </div>
            
            <div className="platform-card card">
              <div className="platform-icon">
                <i className="fas fa-tablet-alt"></i>
              </div>
              <h4>Rakuten Kobo</h4>
              <p>Purchase eBooks for your preferred e-reader</p>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        .books-hero {
          padding: 4rem 0 2rem;
          background: var(--color-bg-secondary);
        }
        
        .books-list {
          padding: 4rem 0;
        }
        
        .book-info {
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        .book-meta {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }
        
        .book-genre, .book-status {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .book-genre {
          background: rgba(196, 167, 125, 0.2);
          color: var(--color-accent);
        }
        
        .book-status {
          background: rgba(100, 200, 100, 0.2);
          color: #64c864;
        }
        
        .book-status.ongoing {
          background: rgba(255, 200, 100, 0.2);
          color: #ffc864;
        }
        
        .platforms-info {
          padding: 4rem 0;
          background: var(--color-bg-primary);
        }
        
        .platform-card {
          text-align: center;
          padding: 2rem 1.5rem;
        }
        
        .platform-icon {
          font-size: 2.5rem;
          margin-bottom: 1rem;
          background: var(--gradient-gold);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .platform-card h4 {
          margin-bottom: 1rem;
        }
      `}</style>
    </>
  );
};

export default Books;